const COLORS = ['#7B2CBF', '#9D4EDD', 'var(--color-success)', 'var(--color-danger)', '#F2B705', '#3B82F6'];
const PARTICLE_COUNT = 70;

/**
 * Deterministic pseudo-random in [0, 1) from a particle index — a pure
 * function, so particles can be computed during render (React purity rules
 * ban `Math.random` there, and a setState-in-effect workaround is worse).
 * Nobody can tell confetti apart from run to run.
 */
function prand(i: number, salt: number): number {
  const v = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return v - Math.floor(v);
}

const PARTICLES = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
  left: prand(i, 1) * 100,
  size: 6 + prand(i, 2) * 6,
  color: COLORS[Math.floor(prand(i, 3) * COLORS.length)],
  delayMs: prand(i, 4) * 500,
  durationMs: 1400 + prand(i, 5) * 1200,
  drift: (prand(i, 6) - 0.5) * 80,
  spin: 360 + prand(i, 7) * 540,
  // Staggered fall depths so the burst fills a tall container, not one band.
  fall: 300 + prand(i, 8) * 320,
}));

/**
 * Dependency-free confetti burst for the FTUE win moment: absolutely
 * positioned falling flakes over the parent (which must be `relative`).
 * Hidden entirely under `prefers-reduced-motion` (pure CSS — no JS check).
 * Hand-rolled on purpose — one celebratory burst doesn't earn a library.
 */
export function ConfettiBurst() {
  return (
    <div data-testid="ftue-confetti" aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
      <style>{`
        @keyframes ftue-confetti-fall {
          /* Pixel fall distance (per-particle --fall): translate % would be
             relative to the tiny flake itself, not the card it rains over. */
          from { transform: translate3d(0, -16px, 0) rotate(0deg); opacity: 1; }
          to   { transform: translate3d(var(--drift), var(--fall), 0) rotate(var(--spin)); opacity: .85; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ftue-confetti-flake { display: none; }
        }
      `}</style>
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          className="ftue-confetti-flake absolute top-0 block"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.45,
            backgroundColor: p.color,
            borderRadius: 1,
            // Custom props consumed by the keyframes above.
            ['--drift' as string]: `${p.drift}px`,
            ['--spin' as string]: `${p.spin}deg`,
            ['--fall' as string]: `${p.fall}px`,
            animation: `ftue-confetti-fall ${p.durationMs}ms ease-in ${p.delayMs}ms both`,
          }}
        />
      ))}
    </div>
  );
}
